import React, { useState, useEffect, useMemo } from 'react'

import { KeyboardArrowDown } from '@mui/icons-material'
import { Button, CircularProgress, DialogTitle, Grid, MenuItem, Select, Typography } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import axios from 'axios'
import format from 'date-fns/format'
import ReactGA from 'react-ga4'
import { useNavigate } from 'react-router-dom'
import { AppConstants, LeadStage } from 'AppConstants/AppConstants'
import Footer from 'components/Footer'
import Header from 'components/Header'
import HorizontalCalendar from 'components/shared/HorizontalCalendar'
import useQueryParams from 'hooks/useQueryParams'
import {
  SetSelectedSlotSignUp,
  SetSelectedStudioSignUp,
  SetSlotBookedAlreadySignUp,
  SetSlotNotFoundSignUp,
} from 'redux/SignUp/persist'
import { useAppDispatch, useAppSelector } from 'redux/store'
import { getBookingCalendarDates } from 'utils/bookingCalendarDates'
import { createNavigateWithQueryParams } from 'utils/navigateWithQueryParams'
import { parseNextAvailableDate, shouldShowNextAvailableButton } from 'utils/nextAvailableDateUtils'
import styles from './Slot.module.scss'

export interface ITime {
  index: number
  value: string
}
interface IScheduleInitialValues {
  appointmentTime: string
  timezone: string
}
const scheduleInitialValues: IScheduleInitialValues = {
  appointmentTime: '',
  timezone: 'America/New_York',
}

const styless: any = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 300,
  height: 170,
  overflow: 'hidden',
  backgroundColor: '#050624',
  color: 'white',
  borderRadius: '8px',
  p: 4,
}

const SlotSignUp = () => {
  const { getAllParams, getParams, hasCampaign } = useQueryParams()
  const utm_source = getParams('utm_source')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Signup flow state management
  const studios = useAppSelector((state) => state.signUpPersist.studiosSignUp)
  const selectedStudio = useAppSelector((state) => state.signUpPersist.selectedStudioSignUp)
  const isMoreThanOneStudio = useAppSelector((state) => state.signUpPersist.isMoreThanOneStudioSignUp)
  const selectedSlot = useAppSelector((state) => state.signUpPersist.selectedSlotSignUp)
  const trackerCount = useAppSelector((state) => state.signUpPersist.trackerCountSignUp)
  const locParam = getParams('loc')
  // Create reusable navigation function
  const navigateWithQueryParams = createNavigateWithQueryParams(navigate, getAllParams)
  const [disableButton, setDisableButton] = useState(true)
  const [loadingButton, setLoadingButton] = useState(false)
  const [loadingContanier, setLoadingContainer] = useState(true)
  const [visitAvailabilities, setVisitAvailabilities] = useState<string[]>([])
  const [selectedDate, setSelectedDate] = useState(selectedSlot ? new Date(selectedSlot?.date) : new Date())
  const [selectedTime, setSelectedTime] = useState<any | null>(selectedSlot ? selectedSlot?.value : null)
  const [selectedIndex, setSelectedIndex] = useState<any | null>(selectedSlot ? selectedSlot?.index : null)
  const [scheduleData, setscheduleData] = useState({
    appointmentTime: selectedSlot?.scheduleObject?.appointmentTime || '',
    timezone: 'America/New_York',
    clinicId: selectedStudio?.id,
  })
  const [deselect, setDeSelect] = useState(false)
  const [apiFailed, setApiFailed] = useState(false)
  const [nextAvailableDate, setNextAvailableDate] = useState<string | null>(null)
  const [noslot, setNoslot] = useState(false)

  const bookingCalendarDates = useMemo(() => getBookingCalendarDates(), [])

  const slotBookedAlready = useAppSelector((state) => state.signUpPersist.slotBookedAlreadySignUp)
  const slotNotFound = useAppSelector((state) => state.signUpPersist.slotNotFoundSignUp)
  const [chosenStudio, setChosenStudio] = useState({ id: '', name: '' })

  const availabileSlots: any = visitAvailabilities

  const timeDetails: any = useMemo(() => {
    return (
      availabileSlots?.map((timeString: any) => {
        const formattedTime = format(new Date(`2000-01-01T${timeString}`), 'HH:mm')
        return formattedTime
      }) || []
    )
  }, [availabileSlots])

  const formattedTimes: string[] =
    timeDetails?.map((timeString: any) => {
      const convertedTime = format(new Date(`2000-01-01T${timeString}`), 'h:mm aa')
      return convertedTime
    }) || []

  const showNextAvailableButton = shouldShowNextAvailableButton({
    noslot,
    nextAvailableDate,
    selectedDate,
    dates: bookingCalendarDates,
  })

  const formatSelectedDateForApi = (date: Date) =>
    date.getFullYear() +
    '-' +
    String(date.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(date.getDate()).padStart(2, '0') +
    ' ' +
    '00:00:00'

  const formattedDate = formatSelectedDateForApi(selectedDate)

  const handleSelect = async (value: any, index: any) => {
    setSelectedTime(value)
    setSelectedIndex(index)
    setDisableButton(false)

    let scheduleTime = timeDetails[index]

    let selectedAppointmentTime = String(
      selectedDate.getFullYear() +
        '-' +
        String(selectedDate.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(selectedDate.getDate()).padStart(2, '0') +
        ' ' +
        scheduleTime,
    )
    setscheduleData({
      ...scheduleData,
      appointmentTime: selectedAppointmentTime,
      clinicId: selectedStudio?.id,
    })
  }

  useEffect(() => {
    if (!selectedStudio) {
      // No studio selected (or session state was lost): restart at studio selection. Never guess a clinic here.
      dispatch(SetSelectedSlotSignUp(null))
      navigateWithQueryParams('/signup/location')
      return
    }
    getClinicAvailability(formattedDate, selectedStudio.id)
  }, [])

  useEffect(() => {
    if (selectedStudio) setChosenStudio({ id: selectedStudio.id, name: selectedStudio.name })
  }, [])

  useEffect(() => {
    if (selectedSlot) {
      let isSlotSelected = formattedTimes?.includes(
        format(new Date(selectedSlot?.scheduleObject.appointmentTime), 'h:mm aa'),
      )

      if (isSlotSelected && !deselect) {
        setDisableButton(false)
      }
    }
  }, [formattedTimes])

  const handleSelectedDate = () => {
    dispatch(SetSelectedSlotSignUp(null))
    setSelectedIndex(null)
    setSelectedTime(null)
    setscheduleData({
      ...scheduleData,
      appointmentTime: '',
      clinicId: selectedStudio?.id,
    })
    setDisableButton(true)
    setSelectedDate((prevSelectedDate: any) => {
      const nextFormattedDate = formatSelectedDateForApi(prevSelectedDate)
      getClinicAvailability(nextFormattedDate, selectedStudio?.id)
      return prevSelectedDate
    })
  }

  const handleGoToNextAvailableDate = () => {
    if (!nextAvailableDate) return

    const nextDate = parseNextAvailableDate(nextAvailableDate)
    if (!nextDate) return

    dispatch(SetSelectedSlotSignUp(null))
    setSelectedIndex(null)
    setSelectedTime(null)
    setDeSelect(false)
    setscheduleData({
      ...scheduleData,
      appointmentTime: '',
      clinicId: selectedStudio?.id,
    })
    setDisableButton(true)
    setSelectedDate(nextDate)

    const clinicId = selectedStudio?.id
    getClinicAvailability(formatSelectedDateForApi(nextDate), clinicId)
  }

  async function getClinicAvailability(formattedDate: any, clinicId: any) {
    setLoadingContainer(true)
    setApiFailed(false)
    // const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const timeZone = 'America/New_York'
    try {
      await axios
        .get(
          AppConstants.API_URL +
            `/visit/clinic/availability?timezone=${timeZone}&clinicId=${clinicId}&date=${formattedDate}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('userToken')}`,
              'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
            },
          },
        )
        .then((res) => {
          setLoadingContainer(false)

          const availabilityData = res?.data?.data || []
          let filteredData: string[] = []
          let seenHours: Record<string, boolean> = {}

          setNextAvailableDate(res?.data?.nextAvailableDate ?? null)
          setNoslot(availabilityData.length <= 0)

          if (availabilityData.length > 0) {
            ;[...availabilityData].reverse().forEach((time: string) => {
              const hour = time.split(':')[0]
              if (!seenHours[hour]) {
                filteredData.push(time)
                seenHours[hour] = true
              }
            })
            filteredData.reverse()
            setNextAvailableDate(null)
          }

          const ghostSlotTime = res?.data?.ghostSlotTime as string | null
          const isGhostSlotActive = !!res?.data?.isGhostSlotActive

          if (isGhostSlotActive && ghostSlotTime) {
            const ghostHour = ghostSlotTime.split(':')[0]
            filteredData = filteredData.filter((time) => time.split(':')[0] !== ghostHour)
            filteredData.push(ghostSlotTime)
            filteredData.sort((a, b) => a.localeCompare(b))
          }

          setVisitAvailabilities(filteredData)
        })
        .catch((err) => {
          if (err) {
            setApiFailed(true)
            setLoadingContainer(false)
            setVisitAvailabilities([])
            setNoslot(true)
            setNextAvailableDate(null)
          }
        })
    } catch (error) {
      console.log(error)
      if (error) {
        setApiFailed(true)
        setLoadingContainer(false)
        setVisitAvailabilities([])
        setNoslot(true)
        setNextAvailableDate(null)
      }
    }
  }

  const postLeadStage = async () => {
    await axios
      .post(
        AppConstants.API_URL + '/lead/stage/update',
        { stage: LeadStage.slot },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('userToken')}`,
            'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
          },
        },
      )
      .then((res) => {
        return res
      })
      .catch((err) => {
        return err?.response
      })
  }

  useEffect(() => {
    postLeadStage()
  }, [])

  useEffect(() => {
    ReactGA.event('page_view', {
      page_title: 'SignUp|Slot',
      page_location: `/signup/slot`,
    })
  }, [])

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <span style={{ flex: 1, marginLeft: '10px', fontSize: 20 }}>{'Pick a slot.'}</span>
          {/* {showClinicAddress && <span className={style.clinicAddress}>{clinicAddress}</span>} */}
          {isMoreThanOneStudio && (
            <Select
              IconComponent={KeyboardArrowDown}
              value={chosenStudio.name}
              onChange={(e) => {
                if (studios) {
                  const pickedStudio = studios?.find((studio) => studio.name === e.target.value)
                  if (pickedStudio) {
                    setChosenStudio({ id: pickedStudio.id, name: pickedStudio.name })
                    dispatch(SetSelectedStudioSignUp(pickedStudio))
                    getClinicAvailability(formattedDate, pickedStudio.id)
                  }
                  dispatch(SetSelectedSlotSignUp(null))
                  setSelectedIndex(null)
                  setSelectedTime(null)
                  setscheduleData({
                    ...scheduleData,
                    appointmentTime: '',
                    clinicId: pickedStudio?.id ?? selectedStudio?.id,
                  })
                  setDisableButton(true)
                }
              }}
              displayEmpty
              sx={{
                width: '164px',
                '& .MuiSelect-select': {
                  fontSize: '14px',
                  '@media (max-width: 700px)': {
                    fontSize: '13px',
                  },
                },
                '@media (max-width: 700px)': {
                  width: '154px',
                },

                height: '31px',
                marginTop: '10px',
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      color: 'var(--color-text-primary)',
                      backgroundColor: 'var(--color-background)',
                      '@media (max-width: 600px)': {
                        minHeight: '33px',
                      },
                      fontSize: '14px',
                    },
                    '& .MuiMenuItem-root.Mui-selected': {
                      backgroundColor: 'var(--color-primary) !important',
                      color: 'var(--color-text-secondary)',
                    },
                    '& .MuiMenuItem-root.Mui-selected:hover': {
                      color: 'var(--color-text-secondary)',
                      backgroundColor: 'var(--color-primary) !important',
                    },
                  },
                },
              }}
            >
              {[...studios]
                ?.sort((a, b) => {
                  if (!locParam) return 0
                  const aMatch = a.slug?.toLowerCase() === locParam.toLowerCase()
                  const bMatch = b.slug?.toLowerCase() === locParam.toLowerCase()
                  if (aMatch && !bMatch) return -1
                  if (!aMatch && bMatch) return 1
                  return 0
                })
                .map((studio) => (
                  <MenuItem key={studio.id} value={studio.name}>
                    {studio.name}
                  </MenuItem>
                ))}
            </Select>
          )}
          <img src={'/logo2.png'} alt="logo" className={styles.logo} />
        </div>
        {/* <Header
          headerText={'Pick a slot.'}
          showBackButton={false}
          onBack={() => {
            if (isMoreThanOneStudio) {
              navigate(`/offers/${id}/select-studio`)
            }
          }}
          clinicAddress={selectedStudio?.name}
          showClinicAddress={isMoreThanOneStudio ? true : false}
        ></Header> */}
        <div className={`${styles.scheduleDetails}`}>
          <div>
            <HorizontalCalendar
              onClick={() => {
                handleSelectedDate()
              }}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
            />

            <div className={styles.toDayDate}>{format(new Date(selectedDate), 'EEEE, MMMM d')}</div>
            <div className={styles.slotContainer}>
              {loadingContanier === true && (
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <CircularProgress style={{ color: '#007f7c' }} size={30}></CircularProgress>
                </div>
              )}
              {formattedTimes.length !== 0 && loadingContanier === false && (
                <Grid columns={1} container spacing={2}>
                  {formattedTimes?.map((value: any, index) => {
                    return (
                      <Grid
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        item
                        xs={6}
                        sm={6}
                        key={index}
                      >
                        <Button
                          className={styles.slotButton}
                          onClick={() => {
                            handleSelect(value, index)
                            if (selectedTime === value) {
                              setSelectedTime(null)
                              setSelectedIndex(null)
                              setDeSelect(true)
                              setscheduleData({
                                ...scheduleData,
                                appointmentTime: '',
                                timezone: 'America/New_York',
                                clinicId: selectedStudio?.id,
                              })
                              setDisableButton(true)
                            }
                          }}
                          sx={{
                            backgroundColor:
                              selectedTime === value ? 'var(--color-primary)' : 'var(--color-background)',
                            border:
                              selectedTime === value
                                ? ' 1px solid var(--color-border-secondary)'
                                : '1px solid var(--color-border-primary)',
                            // height: '56px',
                            // width: '160px',
                            // '@media (min-width: 600px)': {
                            //   width: '140px',
                            //   height: '46px',
                            //   fontSize: '14px',
                            // },
                            color: selectedTime === value ? 'var(--color-text-secondary)' : 'var(--color-text-primary)',
                            fontSize: '16px',
                            boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.20)',
                            '&:hover':
                              selectedTime === value
                                ? { backgroundColor: 'var(--color-primary)' }
                                : { backgroundColor: 'var(--color-background)' },
                          }}
                        >
                          {value}
                        </Button>
                      </Grid>
                    )
                  })}
                </Grid>
              )}
              {loadingContanier === false && !apiFailed && formattedTimes.length === 0 && (
                <div className={styles.noSlotDiv}>
                  <div>
                    <img src={'/images/noSlotsBlack.svg'} alt="no slots"></img>
                  </div>
                  <div className={styles.noSlots}>No slots available</div>
                  <div className={styles.noSlotsText}>Please try a different date or</div>
                  <div className={styles.noSlotsText} style={{ marginTop: '2px' }}>
                    check another location
                  </div>
                </div>
              )}
              {loadingContanier === false && apiFailed && formattedTimes.length === 0 && (
                <div className={styles.noSlotDiv}>
                  <div>
                    <img src={'/images/noSlotsBlack.svg'} alt="no slots"></img>
                  </div>
                  <div className={styles.noSlots}>Something went wrong</div>
                </div>
              )}
            </div>
          </div>
          <div className={styles.btnWrapper}>
            <Grid item xs={12}>
              {slotBookedAlready && (
                <Dialog
                  open={slotBookedAlready}
                  PaperProps={{
                    style: {
                      background: 'var(--color-background)',
                      borderRadius: '12px',
                      padding: '10px 12px',
                      minWidth: 300,
                      maxWidth: 300,
                      minHeight: 'auto',
                      // height: 170,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      flex: 1, // align everything to the left
                      textAlign: 'left', // global text alignment
                    },
                  }}
                >
                  <DialogTitle
                    style={{
                      fontSize: '16px',
                      fontWeight: 700,
                      color: 'var(--color-text-primary)',
                      textAlign: 'left',
                      marginBottom: '12px',
                      padding: 0,
                      width: '100%',
                    }}
                  >
                    Slot Unavailable
                  </DialogTitle>
                  <Typography
                    style={{
                      color: 'var(--color-text-primary)',
                      fontSize: '14px',
                      textAlign: 'left',
                      marginBottom: '24px',
                      lineHeight: 1.5,
                      width: '100%',
                    }}
                  >
                    Your payment has been processed successfully. Unfortunately, the slot you selected is no longer
                    available. Please choose another available slot.
                  </Typography>
                  <Button
                    color="primary"
                    style={{
                      marginBottom: '0.3rem',
                      height: '42px',
                      padding: '0px 8px 0px 8px',
                      fontSize: '14px',
                      alignSelf: 'center',
                    }}
                    onClick={() => {
                      dispatch(SetSlotBookedAlreadySignUp(false))
                    }}
                  >
                    Reschedule
                  </Button>
                </Dialog>
              )}
              {slotNotFound && (
                <Dialog
                  open={slotNotFound}
                  PaperProps={{
                    style: {
                      background: 'var(--color-background)',
                      borderRadius: '12px',
                      padding: '10px 12px',
                      minWidth: 300,
                      maxWidth: 300,
                      minHeight: 'auto',
                      // height: 170,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      flex: 1, // align everything to the left
                      textAlign: 'left', // global text alignment
                    },
                  }}
                >
                  <DialogTitle
                    style={{
                      fontSize: '16px',
                      fontWeight: 700,
                      color: 'var(--color-text-primary)',
                      textAlign: 'left',
                      marginBottom: '12px',
                      padding: 0,
                      width: '100%',
                    }}
                  >
                    Slot Unavailable
                  </DialogTitle>
                  <Typography
                    style={{
                      color: 'var(--color-text-primary)',
                      fontSize: '14px',
                      textAlign: 'left',
                      marginBottom: '24px',
                      lineHeight: 1.5,
                      width: '100%',
                    }}
                  >
                    Your payment has been processed successfully. Unfortunately, the slot you selected is no longer
                    available. Please choose another available slot.
                  </Typography>
                  <Button
                    color="primary"
                    style={{
                      marginBottom: '0.3rem',
                      height: '42px',
                      padding: '0px 8px 0px 8px',
                      fontSize: '14px',
                      alignSelf: 'center',
                    }}
                    onClick={() => {
                      dispatch(SetSlotNotFoundSignUp(false))
                    }}
                  >
                    Reschedule
                  </Button>
                </Dialog>
              )}
            </Grid>
          </div>
        </div>
        <Footer
          buttonText={showNextAvailableButton ? 'See Next Available Day' : 'Continue'}
          isLoading={loadingButton}
          trackerIndex={!hasCampaign ? 5 : locParam ? 4 : 5}
          shouldShowFour={true}
          shouldShowFive={true}
          shouldShowSix={true}
          shouldShowSeven={trackerCount >= 7}
          shouldShowEight={trackerCount >= 8}
          isDisabled={showNextAvailableButton ? false : disableButton}
          buttonType={'button'}
          onButtonClick={() => {
            if (showNextAvailableButton) {
              handleGoToNextAvailableDate()
              return
            }

            if (!disableButton && !loadingButton) {
              if (scheduleData) {
                dispatch(
                  SetSelectedSlotSignUp({
                    scheduleObject: scheduleData,
                    index: selectedIndex,
                    value: selectedTime,
                    date: selectedDate,
                  }),
                )
                if (!hasCampaign) {
                  navigateWithQueryParams('/signup/plan')
                } else if (hasCampaign) {
                  navigateWithQueryParams('/signup/confirmation')
                }
              }
            }
          }}
        />
      </div>
    </>
  )
}

export default SlotSignUp
